import { useEffect, useState } from "react";
import { HtmlPreview } from "./HtmlPreview.js";
import { QuestionPreview } from "./QuestionPreview.js";
import { Notice } from "./components.js";
import { isLikelyMarkdown, markdownDocument } from "./markdown.js";

export type ResponseType = "html" | "json" | "markdown";

/**
 * Renders generated content according to its category or step response type,
 * with intelligent fallback detection for Markdown in multi-step pipeline outputs.
 *
 * Intermediate pipeline steps often generate markdown drafts even when the
 * collection or pipeline defaults to an "html" response_type. ContentPreview
 * auto-detects markdown structures and provides tabs allowing reviewers to switch
 * between Rendered (Markdown), Rendered (HTML), and Raw Source views.
 */
export function ContentPreview({
  content,
  responseType,
  questionAssessmentType,
  showSourceTab = true,
}: {
  content: string;
  responseType: ResponseType;
  questionAssessmentType?: string | null;
  /** The review flow already shows raw source in its CodeMirror editor, so
   *  a separate read-only Source tab is redundant there — callers inside
   *  ContentReviewSection/SourcedContentView pass false to hide it and go
   *  straight to Rendered. Defaults to true for every other caller. */
  showSourceTab?: boolean;
}) {
  if (questionAssessmentType) {
    return <QuestionPreview content={content} />;
  }

  // Detect format when responseType is "html" but output is actually Markdown or JSON
  const detectedFormat: "html" | "markdown" | "json" = (() => {
    if (responseType === "json") return "json";
    if (responseType === "markdown") return "markdown";
    if (isLikelyMarkdown(content)) return "markdown";
    const trimmed = content.trim();
    if (
      (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
      (trimmed.startsWith("[") && trimmed.endsWith("]"))
    ) {
      try {
        JSON.parse(trimmed);
        return "json";
      } catch {
        // not json
      }
    }
    return "html";
  })();

  const [activeTab, setActiveTab] = useState<"rendered" | "source">("rendered");
  const [formatMode, setFormatMode] = useState<"html" | "markdown" | "json">(detectedFormat);

  useEffect(() => {
    setFormatMode(detectedFormat);
  }, [content, responseType]);

  function renderMainContent() {
    if (showSourceTab && activeTab === "source") {
      return (
        <pre className="preview" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {content}
        </pre>
      );
    }

    if (formatMode === "json") {
      try {
        const parsed: unknown = JSON.parse(content);
        return (
          <pre className="preview" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {JSON.stringify(parsed, null, 2)}
          </pre>
        );
      } catch {
        return (
          <>
            <Notice kind="error">This is not valid JSON, despite the category's schema.</Notice>
            <pre className="preview">{content}</pre>
          </>
        );
      }
    }

    if (formatMode === "markdown") {
      return <HtmlPreview html={markdownDocument(content)} />;
    }

    return <HtmlPreview html={content} />;
  }

  return (
    <div>
      <div className="row between" style={{ gap: 8, marginBottom: 8, flexWrap: "wrap", alignItems: "center" }}>
        {showSourceTab && (
          <div className="row" style={{ gap: 6 }}>
            <button
              type="button"
              className={`btn mini${activeTab === "rendered" ? " active" : ""}`}
              onClick={() => setActiveTab("rendered")}
            >
              Rendered
            </button>
            <button
              type="button"
              className={`btn mini${activeTab === "source" ? " active" : ""}`}
              onClick={() => setActiveTab("source")}
            >
              Source
            </button>
          </div>
        )}

        {(!showSourceTab || activeTab === "rendered") && (
          <div className="row" style={{ gap: 4, alignItems: "center" }}>
            <span className="muted mini" style={{ marginRight: 4 }}>Format:</span>
            <button
              type="button"
              className={`btn mini${formatMode === "markdown" ? " primary" : ""}`}
              onClick={() => setFormatMode("markdown")}
            >
              Markdown
            </button>
            <button
              type="button"
              className={`btn mini${formatMode === "html" ? " primary" : ""}`}
              onClick={() => setFormatMode("html")}
            >
              HTML
            </button>
            {(responseType === "json" || formatMode === "json") && (
              <button
                type="button"
                className={`btn mini${formatMode === "json" ? " primary" : ""}`}
                onClick={() => setFormatMode("json")}
              >
                JSON
              </button>
            )}
          </div>
        )}
      </div>

      {renderMainContent()}
    </div>
  );
}
